<template>
  <div class="pg-select">
    <!-- 섹션 레벨: 제목 / 서브타입 -->
    <div class="p-form-grid p-form-grid--2">
      <div class="p-field">
        <label class="p-field__label">Section Title</label>
        <input
          v-model="sectionData.content.sectionTitle"
          type="text"
          class="p-input"
          placeholder="섹션 제목"
        />
      </div>
      <div class="p-field">
        <label class="p-field__label">Prompt Type (서브타입 · 전체 공통)</label>
        <select v-model="sectionData.content.practiceSubtype" class="p-input">
          <option value="audio">Audio — 음성 듣고 선택</option>
          <option value="image">Image — 그림 보고 선택</option>
          <option value="blank">Blank — 빈칸 채우기</option>
        </select>
      </div>
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-field">
        <label class="p-field__label">Question / 지시문</label>
        <input
          v-model="current.questionText"
          type="text"
          class="p-input"
          placeholder="예: 음성을 듣고 알맞은 직업을 고르세요"
        />
      </div>

      <div class="p-field">
        <label class="p-field__label">보기 문장 (지문)</label>
        <textarea
          v-model="current.passage"
          class="p-input pg-select__passage"
          rows="3"
          placeholder="보기로 제시할 문장/지문을 입력하세요 (선택)"
        ></textarea>
      </div>

      <!-- 프롬프트 영역 (서브타입별) -->
      <div class="pg-select__prompt">
        <!-- audio: 문제별 음성 URL -->
        <div
          v-if="sectionData.content.practiceSubtype === 'audio'"
          class="pg-select__prompt-audio"
        >
          <label class="p-field__label">
            <i class="material-icons-round pg-select__audio-ico">music_note</i>
            Audio URL (문제별 음성)
          </label>
          <input
            v-model="current.audioUrl"
            type="text"
            class="p-input"
            placeholder="음성 URL (풀 URL 권장)"
          />
        </div>

        <!-- image: 상황 삽화 URL -->
        <div
          v-else-if="sectionData.content.practiceSubtype === 'image'"
          class="pg-select__prompt-image"
        >
          <label class="p-field__label">Prompt Image URL (상황 삽화)</label>
          <input
            v-model="current.promptImage"
            type="text"
            class="p-input"
            placeholder="이미지 URL을 입력하세요 (풀 URL 권장)"
          />
          <div v-if="current.promptImage" class="pg-select__prompt-preview">
            <img :src="current.promptImage" alt="prompt" @error="onPreviewError" />
          </div>
        </div>

        <!-- blank: 문장 빈칸 채우기 -->
        <div
          v-else-if="sectionData.content.practiceSubtype === 'blank'"
          class="pg-select__prompt-blank"
        >
          <label class="p-field__label">Sentence (빈칸은 <code>___</code> 언더스코어 2개 이상으로 표시)</label>
          <input
            v-model="current.sentence"
            type="text"
            class="p-input"
            placeholder="예: 수업이 ___ ."
          />
          <div v-if="current.sentence" class="pg-select__blank-preview">
            <span class="pg-select__blank-label">미리보기</span>
            <span class="pg-select__blank-text">{{ blankPreview }}</span>
          </div>
        </div>
      </div>

      <!-- 보기(Options) · 텍스트 전용 -->
      <div class="pg-select__options">
        <div
          v-for="(option, index) in current.options"
          :key="index"
          class="pg-select__item"
          :class="{ 'is-answer': current.answer === index }"
        >
          <div class="pg-select__item-head">
            <label class="pg-select__answer" :class="{ 'is-on': current.answer === index }">
              <input
                type="radio"
                :name="answerRadioName"
                :value="index"
                v-model="current.answer"
              />
              <i class="material-icons-round">{{ current.answer === index ? 'check_circle' : 'radio_button_unchecked' }}</i>
              <span>정답</span>
            </label>
            <span class="pg-select__index">Option {{ index + 1 }}</span>
            <button
              type="button"
              class="pg-select__icon-btn pg-select__icon-btn--danger"
              title="제거"
              @click.prevent="removeOption(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
          <div class="pg-select__item-body">
            <div class="p-field">
              <label class="p-field__label">Option Text</label>
              <input
                v-model="option.option_text"
                type="text"
                class="p-input"
                placeholder="보기 텍스트"
              />
            </div>
            <div class="pg-select__image">
              <label class="p-field__label">Option Image (선택)</label>
              <image-upload
                :default-image="option.option_image || require('@/assets/img/no_image.png')"
                :unique-id="'select-option-image-' + index"
                v-model="option.option_image"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pg-select__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addOption">Option 추가</ABtn>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ImageUpload from "@/components/ImageUpload.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  passage: "",
  audioUrl: "",
  promptImage: "",
  sentence: "",
  options: [{ option_text: "", option_image: "" }],
  answer: null
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "passage", "options", "answer", "promptImage", "sentence", "audioUrl"]
);

const answerRadioName = computed(() => `select-answer-${currentIndex.value}`);

const blankPreview = computed(() => {
  if (!current.value) return "";
  const sentence = current.value.sentence || "";
  const answer = current.value.answer;
  const options = current.value.options || [];
  const filled =
    typeof answer === "number" && options[answer]
      ? options[answer].option_text || "□"
      : "□";
  return sentence.replace(/_{2,}/, `[ ${filled} ]`);
});

const addOption = () => {
  current.value.options.push({ option_text: "", option_image: "" });
};

const removeOption = (index) => {
  current.value.options.splice(index, 1);
  const answer = current.value.answer;
  if (answer === index) {
    current.value.answer = null;
  } else if (typeof answer === "number" && answer > index) {
    current.value.answer = answer - 1;
  }
};

const onPreviewError = (event) => {
  event.target.src = require("@/assets/img/no_image.png");
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-select {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__passage {
    resize: vertical;
    min-height: 72px;
    line-height: 1.5;
  }

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }

  // ── 프롬프트 ──
  &__prompt {
    display: flex;
  }

  &__prompt-audio,
  &__prompt-image,
  &__prompt-blank {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  &__prompt-blank code {
    font-family: $p-font-mono;
    font-size: 11px;
    padding: 1px 5px;
    border-radius: 4px;
    background: $p-panel-alt;
    color: $p-point-deep;
  }

  &__prompt-preview {
    max-width: 280px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    background: $p-bg;

    > img {
      display: block;
      width: 100%;
      max-height: 200px;
      object-fit: contain;
    }
  }

  &__blank-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__blank-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__blank-text {
    font-size: 15px;
    font-weight: 600;
    color: $p-ink;
  }

  // ── 보기 ──
  &__options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }

    &.is-answer {
      border-color: $p-ok;
      background: $p-ok-soft;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__answer {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    user-select: none;

    > input { display: none; }
    > i { font-size: 18px; color: $p-ink-soft; }

    &.is-on {
      color: $p-ok;
      > i { color: $p-ok; }
    }
  }

  &__index {
    margin-left: auto;
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 1fr 200px;
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__image {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
